// The local store in IndexedDB, one database per signed-in account ("bs-sync-<account>"). Every tab
// opens the same database, and IndexedDB serialises their read-write transactions, so the clock and
// the outbox stay consistent across tabs without any locking of our own.
import type { Store, Tx } from "./store";
import type { Collection, DeadRow, LocalEntity, OutboxRow, SyncMeta } from "./types";

const VERSION = 1;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function open(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(name, VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      db.createObjectStore("meta");
      const ents = db.createObjectStore("entities", { keyPath: ["collection", "id"] });
      ents.createIndex("collection", "collection");
      const out = db.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
      out.createIndex("entity", ["collection", "id"]);
      db.createObjectStore("dead", { autoIncrement: true });
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.onblocked = () => reject(new Error("The local store is busy in another tab."));
  });
}

const STORES = ["meta", "entities", "outbox", "dead"];

export function idbStore(account: string): Store {
  const name = `bs-sync-${account}`;
  let db: Promise<IDBDatabase> | null = null;
  const get = () => {
    db ??= open(name).then((d) => {
      // Another tab deleting the database (sign-out) closes ours.
      d.onversionchange = () => {
        d.close();
        db = null;
      };
      return d;
    });
    return db;
  };

  return {
    async tx(mode, fn) {
      const d = await get();
      const t = d.transaction(STORES, mode);
      const meta = t.objectStore("meta");
      const ents = t.objectStore("entities");
      const out = t.objectStore("outbox");
      const dead = t.objectStore("dead");
      const done = new Promise<void>((resolve, reject) => {
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error ?? new Error("The local store refused the change."));
      });
      const tx: Tx = {
        getMeta: () => req(meta.get("meta") as IDBRequest<SyncMeta | undefined>),
        putMeta: (m) => void meta.put(m, "meta"),
        getEntity: (c: Collection, id: string) => req(ents.get([c, id]) as IDBRequest<LocalEntity | undefined>),
        putEntity: (e) => void ents.put(e),
        deleteEntity: (c, id) => void ents.delete([c, id]),
        entities: (c) => req((c ? ents.index("collection").getAll(c) : ents.getAll()) as IDBRequest<LocalEntity[]>),
        outbox: () => req(out.getAll() as IDBRequest<OutboxRow[]>),
        outboxFor: (c, id) => req(out.index("entity").getAll([c, id]) as IDBRequest<OutboxRow[]>).then((rows) => rows.sort((a, b) => a.seq - b.seq)),
        putOutbox: (row) => void (row.seq === undefined ? out.add(withoutSeq(row)) : out.put(row)),
        deleteOutbox: (seq) => void out.delete(seq),
        putDead: (row: DeadRow) => void dead.add(row),
        dead: () => req(dead.getAll() as IDBRequest<DeadRow[]>),
        clear: () => STORES.forEach((s) => t.objectStore(s).clear()),
      };
      try {
        const result = await fn(tx);
        if (mode === "readonly") return result;
        await done;
        return result;
      } catch (e) {
        try {
          t.abort();
        } catch {
          // Already finished.
        }
        throw e;
      }
    },
    async destroy() {
      const d = await db?.catch(() => null);
      d?.close();
      db = null;
      await new Promise<void>((resolve) => {
        const r = indexedDB.deleteDatabase(name);
        r.onsuccess = r.onerror = r.onblocked = () => resolve();
      });
    },
  };
}

/** An autoIncrement store's `add` must not carry an undefined key field. */
function withoutSeq<T extends { seq?: number }>(row: T): Omit<T, "seq"> {
  const { seq: _seq, ...rest } = row;
  void _seq;
  return rest;
}

export const hasIndexedDb = () => typeof indexedDB !== "undefined";
